/**
 * Catálogo de permissões do sistema.
 *
 * Permissões são fixas no código; perfis (roles) agrupam permissões e são editáveis nas telas.
 * As da base ficam aqui; as de cada módulo instalado, no `index.ts` do módulo, e entram pela lista
 * gerada `modules/index.ts` (ADR 0123). Nunca renomeie um código já em uso: crie um novo e migre
 * os perfis (ver docs/adr/0006-login-e-permissoes.md).
 */

import { MODULE_PERMISSION_MODULES, MODULE_PERMISSIONS } from './modules/index.ts';

export type LocalizedText = { 'pt-BR': string; en: string };

export interface PermissionDefinition {
  code: string;
  module: string;
  label: LocalizedText;
  /**
   * Ações sensíveis (aprovações, bloqueios, dados financeiros, pessoais e de administração).
   * Exigem a segunda etapa de login (ADR 0020).
   */
  sensitive?: boolean;
  /**
   * Nunca vem automaticamente no perfil Administrador: só por atribuição explícita a quem foi
   * autorizado (ex.: confirmar dados bancários, só a Tesouraria). Um administrador
   * também pode recebê-la, por outro perfil atribuído a ele.
   */
  explicitOnly?: boolean;
}

export const PERMISSION_MODULES: Record<string, LocalizedText> = {
  ...MODULE_PERMISSION_MODULES,
  iam: { 'pt-BR': 'Usuários e acessos', en: 'Users and access' },
  audit: { 'pt-BR': 'Auditoria', en: 'Audit' },
  platform: { 'pt-BR': 'Plataforma', en: 'Platform' },
};

const BASE_PERMISSIONS = [
  {
    code: 'iam.user.read',
    module: 'iam',
    label: { 'pt-BR': 'Ver usuários', en: 'View users' },
  },
  {
    code: 'iam.user.manage',
    module: 'iam',
    label: {
      'pt-BR': 'Cadastrar e desativar usuários',
      en: 'Create and deactivate users',
    },
    sensitive: true,
  },
  {
    code: 'iam.access.grant',
    module: 'iam',
    label: {
      'pt-BR': 'Conceder e retirar perfis (nunca para si mesmo)',
      en: 'Grant and remove roles (never to yourself)',
    },
    sensitive: true,
  },
  {
    code: 'iam.role.read',
    module: 'iam',
    label: { 'pt-BR': 'Ver perfis de acesso', en: 'View roles' },
  },
  {
    code: 'iam.role.manage',
    module: 'iam',
    label: { 'pt-BR': 'Criar e alterar perfis de acesso', en: 'Create and edit roles' },
    sensitive: true,
  },
  {
    code: 'audit.log.read',
    module: 'audit',
    label: {
      'pt-BR': 'Ver o histórico de alterações dos cadastros',
      en: 'View the change history of the records',
    },
    sensitive: true,
  },
  {
    code: 'audit.access.read',
    module: 'audit',
    label: {
      'pt-BR': 'Ver os eventos de segurança (entradas, recusas e operações sensíveis)',
      en: 'View security events (sign-ins, refusals and sensitive operations)',
    },
    sensitive: true,
  },
  {
    code: 'platform.settings.manage',
    module: 'platform',
    label: {
      'pt-BR': 'Configurar a aparência do sistema (nome, logo e cores)',
      en: 'Configure the system appearance (name, logo and colors)',
    },
  },
  {
    // Só mostra o chat; quem decide o que a IA pode fazer é o Smart Builder (ADR 0125).
    code: 'platform.ai.chat',
    module: 'platform',
    label: {
      'pt-BR': 'Pedir mudanças no sistema conversando com a IA',
      en: 'Ask for changes to the system by talking to the AI',
    },
  },
] as const satisfies readonly PermissionDefinition[];

export const PERMISSIONS = [
  ...BASE_PERMISSIONS,
  ...MODULE_PERMISSIONS,
] as const satisfies readonly PermissionDefinition[];

export type PermissionCode = (typeof PERMISSIONS)[number]['code'];

export const PERMISSION_CODES: readonly PermissionCode[] = PERMISSIONS.map((p) => p.code);

/** O que o perfil Administrador recebe automaticamente: tudo, menos as permissões explícitas. */
export const ADMIN_PERMISSION_CODES: readonly PermissionCode[] = PERMISSIONS.filter(
  (p) => !(p as PermissionDefinition).explicitOnly,
).map((p) => p.code);

/**
 * Permissões sensíveis: exigem a segunda etapa de login (ADR 0020). Sem ela, a
 * sessão perde essas permissões no servidor e no banco, e as telas orientam a ativá-la.
 */
export const SENSITIVE_PERMISSION_CODES: readonly PermissionCode[] = PERMISSIONS.filter(
  (p) => !!(p as PermissionDefinition).sensitive,
).map((p) => p.code);

export function isPermissionCode(value: string): value is PermissionCode {
  return (PERMISSION_CODES as readonly string[]).includes(value);
}

/** Um perfil atribuído à pessoa: vale no sistema inteiro. */
export interface RoleGrant {
  permissions: readonly string[];
}

/** Algum dos perfis da pessoa dá a permissão? */
export function hasPermission(grants: readonly RoleGrant[], permission: PermissionCode): boolean {
  return grants.some((g) => g.permissions.includes(permission));
}
